import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';

const SITE_URL = (typeof import.meta !== 'undefined' && (import.meta as Record<string, unknown>).env?.VITE_SITE_URL as string) || '';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${SITE_URL}/contact#contactpage`,
  name: '聯絡方式 | 得運智科股份有限公司',
  url: `${SITE_URL}/contact`,
  description: '想了解政府資源、AI 導入、財稅管理或 ESG 永續規劃？歡迎與得運智科聯繫，預約您的專屬諮詢。',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首頁', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '聯絡方式', item: `${SITE_URL}/contact` },
    ],
  },
  mainEntity: {
    '@type': 'Organization',
    name: '得運智科股份有限公司',
    url: SITE_URL,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '市政北二路 236 號 20 樓 15 室',
      addressLocality: '台中市',
      addressRegion: '西屯區',
      postalCode: '407',
      addressCountry: 'TW',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+886-909-518-828',
      contactType: 'customer service',
      areaServed: 'TW',
      availableLanguage: ['Chinese'],
    },
    telephone: '+886-909-518-828',
    email: 'abc10616@gmail.com',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 24.1632,
      longitude: 120.6473,
    },
  },
};

const serviceOptions = [
  '產官學資源整合與育成',
  '財稅與管理顧問',
  'AI 提示工程與數位轉型',
  'ESG 碳管理與稽核',
  '其他',
];

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    service: '',
    message: '',
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.target as HTMLFormElement;
    const formDataObj = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d8fenjh5ptqq13rvl6a0', {
        method: 'POST',
        body: formDataObj,
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', company: '', phone: '', email: '', service: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="聯絡方式 | 得運智科股份有限公司"
        description="聯絡得運智科股份有限公司，預約您的專屬諮詢。地址：台中市西屯區市政北二路 236 號 20 樓 15 室，電話：0909-518-828。提供產官學資源整合、財稅管理、AI 數位轉型、ESG 永續等諮詢服務。"
        keywords="聯絡得運智科, 企業顧問諮詢, 預約諮詢, 台中企業顧問, 企業轉型諮詢"
        canonicalPath="/contact"
        ogType="website"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        {/* Hero with background image */}
        <section className="relative min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_d2b75957b2c988904079fe68659fe09a_066652cf.jpg"
              alt="聯絡方式背景"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-900/35 via-primary-900/20 to-primary-900/45" />
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 text-center pt-16">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              聯絡方式
            </h1>
            <p className="text-sm md:text-base text-white/90 max-w-2xl mx-auto leading-relaxed md:whitespace-nowrap md:max-w-4xl">
              想了解政府資源、AI 導入、財稅管理或 ESG 永續規劃？歡迎與得運智科聯繫，預約您的專屬諮詢。
            </p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
              {/* Form */}
              <div className="w-full lg:w-3/5">
                <div className="mb-8">
                  <span className="text-xs font-semibold text-foreground-700 tracking-wider uppercase">
                    GET IN TOUCH
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-2">
                    預約您的專屬諮詢
                  </h2>
                  <p className="text-sm text-foreground-700 mt-2">
                    請留下您的聯絡資訊與需求簡述，我們將協助您初步判斷適合的服務方向，並安排後續諮詢。
                  </p>
                </div>

                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-foreground-800 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full h-12 px-4 rounded-lg border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                        placeholder="請輸入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-foreground-800 mb-1.5">
                        公司名稱
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full h-12 px-4 rounded-lg border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                        placeholder="請輸入公司名稱"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-foreground-800 mb-1.5">
                        聯絡電話
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full h-12 px-4 rounded-lg border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                        placeholder="請輸入聯絡電話"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-foreground-800 mb-1.5">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full h-12 px-4 rounded-lg border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                        placeholder="請輸入 Email"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-semibold text-foreground-800 mb-1.5">
                      想諮詢的服務項目
                    </label>
                    <select
                      id="service"
                      name="service"
                      required
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full h-12 px-4 rounded-lg border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all bg-white"
                    >
                      <option value="">請選擇服務項目</option>
                      {serviceOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-foreground-800 mb-1.5">
                      需求簡述
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all resize-none"
                      placeholder="請簡述您的需求或問題（最多 500 字）"
                    />
                    <p className="text-xs text-foreground-500 mt-1 text-right">
                      {formData.message.length}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full h-14 bg-primary-600 text-white rounded-full text-base font-semibold hover:bg-primary-700 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap shadow-md shadow-primary-600/20"
                  >
                    {formStatus === 'submitting' ? '送出中...' : '送出諮詢需求'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 text-center">
                        感謝您的諮詢需求，我們將盡快與您聯繫！
                      </p>
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 text-center">
                        送出失敗，請稍後再試或直接使用電話聯繫。
                      </p>
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Info */}
              <div className="w-full lg:w-2/5">
                <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200">
                  <h3 className="text-lg font-bold text-foreground-950 mb-6">
                    聯絡資訊
                  </h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">地址</p>
                        <p className="text-sm text-foreground-700 mt-0.5 leading-relaxed">
                          台中市西屯區市政北二路 236 號 20 樓 15 室（七期館）
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-primary-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">電話</p>
                        <p className="text-sm text-foreground-700 mt-0.5">0909-518-828</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-primary-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">市話</p>
                        <p className="text-sm text-foreground-700 mt-0.5">04-2254-3365</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">Email</p>
                        <p className="text-sm text-foreground-700 mt-0.5">abc10616@gmail.com</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="mt-6 rounded-xl overflow-hidden border border-background-200">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3639.567!2d120.6473!3d24.1632!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDA5JzQ3LjUiTiAxMjDCsDM4JzUwLjMiRQ!5e0!3m2!1szh-TW!2stw!4v1"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="得運智科位置"
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}